import React, { useEffect, useState, useRef } from 'react';
import useEditFormScroll, { editHighlightStyle } from '../hooks/useEditFormScroll';

function KotItemMaster({ token, selectedProperty, selectedOutletId = '' }) {
  const [outlets, setOutlets] = useState([]);
  const [scope, setScope] = useState({
    chain_id: selectedProperty?.chain_id || '',
    property_id: selectedProperty?.id || '',
    outlet_id: selectedOutletId || ''
  });
  const [rows, setRows] = useState([]);
  const [taxRows, setTaxRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    sku: '',
    name: '',
    base_price: 0,
    category_id: '',
    tax_id: '',
    is_veg: false,
    is_active: true,
    image_url: '',
    is_bestseller: false,
    is_spicy: false,
    is_jain: false
  });
  const [editingId, setEditingId] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // REQ-04 bulk update
  const [selectedIds, setSelectedIds] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ scope: 'ALL', operation: 'PRICE_CHANGE', category_id: '', tax_id: '', price_mode: 'PERCENT', price_value: '', rounding: 'NONE', target_category_id: '', is_active: true });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const { formRef, editHighlight, scrollToForm } = useEditFormScroll();

  const scopeHeaders = () => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': scope.chain_id,
    'x-property-id': scope.property_id,
    'x-outlet-id': scope.outlet_id
  });

  const loadOutlets = async (chainId, propertyId) => {
    if (!chainId || !propertyId) return;
    try {
      const res = await fetch(`/api/v1/core/outlets?property_id=${encodeURIComponent(propertyId)}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load outlets');
      const all = Array.isArray(data) ? data : [];
      const list = all.filter(o => String(o.outlet_type || '').toUpperCase() !== 'BAR' || o.module_kot_enabled === true);
      setOutlets(list);
      if (list.length > 0) {
        const match = selectedOutletId && list.find((o) => String(o.id) === String(selectedOutletId));
        setScope((prev) => ({ ...prev, outlet_id: match ? match.id : list[0].id }));
      } else {
        setScope((prev) => ({ ...prev, outlet_id: '' }));
      }
    } catch (err) {
      setError(err?.message || 'Network error while loading outlets');
    }
  };

  const loadRows = async () => {
    if (!scope.chain_id || !scope.property_id || !scope.outlet_id) return setRows([]);
    try {
      const res = await fetch('/api/v1/kot/masters/menu-items?all=1', { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load menu items');
      setRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading menu items');
    }
  };

  const loadTaxRows = async () => {
    if (!scope.property_id) return setTaxRows([]);
    try {
      const res = await fetch('/api/tax-master', { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load tax profiles');
      setTaxRows(Array.isArray(data) ? data : []);
    } catch {
      setError('Network error while loading tax profiles');
    }
  };

  const loadCategories = async () => {
    if (!scope.property_id) return setCategories([]);
    try {
      const res = await fetch('/api/v1/kot/masters/food-categories', { headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      setCategories(Array.isArray(data) ? data.filter(c => c.is_active !== false) : []);
    } catch {
      // non-critical — category dropdown just stays empty
    }
  };

  const downloadTemplate = async () => {
    setError('');
    try {
      const res = await fetch('/api/v1/kot/masters/menu-items/import/template', { headers: scopeHeaders() });
      if (!res.ok) { const d = await res.json().catch(() => ({})); return setError(d.error || 'Failed to download template'); }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'kot_item_master_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Template download failed');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!scope.outlet_id) return setError('Select an outlet before importing');
    setError(''); setMessage(''); setImportResult(null); setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Note: scopeHeaders() has no Content-Type, so the browser sets the multipart boundary.
      const res = await fetch('/api/v1/kot/masters/menu-items/import', { method: 'POST', headers: scopeHeaders(), body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Import failed');
      setImportResult(data);
      setMessage(`Import complete — ${data.created} added, ${data.updated} updated, ${data.categories_created} new categor${data.categories_created === 1 ? 'y' : 'ies'}${data.errors?.length ? `, ${data.errors.length} skipped` : ''}.`);
      await loadRows();
      await loadCategories();
    } catch (err) {
      setError(err?.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const updateBulk = (patch) => { setBulk((prev) => ({ ...prev, ...patch })); setBulkPreview(null); };
  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  // Items list search: name, SKU or tag, case-insensitive.
  const searchTerm = itemSearch.trim().toLowerCase();
  const visibleRows = searchTerm
    ? rows.filter((r) => [r.name, r.sku, Array.isArray(r.tags) ? r.tags.join(' ') : r.tags]
        .some((v) => String(v || '').toLowerCase().includes(searchTerm)))
    : rows;
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.includes(r.id));
  // Select-all acts on the rows currently shown, so a search can feed Bulk Update.
  const toggleSelectAll = () => setSelectedIds((prev) => {
    const visibleIds = visibleRows.map((r) => r.id);
    return allVisibleSelected
      ? prev.filter((id) => !visibleIds.includes(id))
      : [...new Set([...prev, ...visibleIds])];
  });

  const runBulk = async (dryRun) => {
    if (!scope.outlet_id) return setError('Select an outlet first');
    const body = { scope: bulk.scope, operation: bulk.operation, dry_run: dryRun };
    if (bulk.scope === 'CATEGORY') { if (!bulk.category_id) return setError('Pick a category'); body.category_id = Number(bulk.category_id); }
    if (bulk.scope === 'ITEMS') { if (!selectedIds.length) return setError('Tick at least one item in the list'); body.item_ids = selectedIds; }
    if (bulk.operation === 'SET_TAX') body.tax_id = bulk.tax_id ? Number(bulk.tax_id) : null;
    if (bulk.operation === 'PRICE_CHANGE') {
      if (bulk.price_value === '' || Number.isNaN(Number(bulk.price_value))) return setError('Enter a price value');
      body.price_mode = bulk.price_mode; body.price_value = Number(bulk.price_value); body.rounding = bulk.rounding;
    }
    if (bulk.operation === 'SET_CATEGORY') { if (!bulk.target_category_id) return setError('Pick a target category'); body.target_category_id = Number(bulk.target_category_id); }
    if (bulk.operation === 'SET_ACTIVE') body.is_active = bulk.is_active;
    setError(''); setMessage(''); setBulkBusy(true);
    try {
      const res = await fetch('/api/v1/kot/masters/menu-items/bulk-update', { method: 'POST', headers: { ...scopeHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Bulk update failed');
      setBulkPreview(data);
      if (!dryRun) {
        setMessage(`Bulk ${data.operation} applied to ${data.affected_count} item(s).`);
        setSelectedIds([]);
        await loadRows();
      }
    } catch (e) {
      setError(e?.message || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  useEffect(() => {
    if (selectedProperty?.chain_id && selectedProperty?.id) {
      setScope((prev) => ({ ...prev, chain_id: selectedProperty.chain_id, property_id: selectedProperty.id, outlet_id: selectedOutletId || '' }));
    } else {
      setScope({ chain_id: '', property_id: '', outlet_id: '' });
      setOutlets([]);
      setRows([]);
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedOutletId && outlets.some((o) => String(o.id) === String(selectedOutletId))) {
      setScope((prev) => ({ ...prev, outlet_id: selectedOutletId }));
    }
  }, [selectedOutletId]);

  useEffect(() => { if (scope.chain_id && scope.property_id) { loadOutlets(scope.chain_id, scope.property_id); loadTaxRows(); loadCategories(); } }, [scope.chain_id, scope.property_id, token]);
  useEffect(() => { loadRows(); }, [scope.outlet_id]);

  /* ── Image validation, auto-resize & upload ── */
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const TARGET_SIZE = 400; // px — auto-resize to 400x400 square

  const resizeImageToSquare = (img, size) => {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    // Center-crop to square
    const minDim = Math.min(img.width, img.height);
    const sx = (img.width - minDim) / 2;
    const sy = (img.height - minDim) / 2;
    ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, size, size);
    return canvas;
  };

  const canvasToFile = (canvas, fileName) => {
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(new File([blob], fileName, { type: 'image/jpeg' }));
        } else {
          resolve(null);
        }
      }, 'image/jpeg', 0.85);
    });
  };

  const validateAndPreviewImage = (file) => {
    setError('');
    if (!file) return;

    // Extension check
    const ext = (file.name || '').split('.').pop().toLowerCase();
    const allowedExts = ['jpg', 'jpeg', 'png', 'webp'];
    if (!allowedExts.includes(ext)) {
      setError('Invalid file type. Only JPG, PNG, or WebP images are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // MIME type check
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Invalid file type. Only JPG, PNG, or WebP images are allowed.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // File size check — max 2 MB
    if (file.size > 2 * 1024 * 1024) {
      setError('File size exceeds the 2 MB limit. Please choose a smaller image.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Auto-resize: load image, crop to square, resize to TARGET_SIZE
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      const canvas = resizeImageToSquare(img, TARGET_SIZE);
      const previewDataUrl = canvas.toDataURL('image/jpeg', 0.85);
      setImagePreview(previewDataUrl);
      const resizedFile = await canvasToFile(canvas, file.name.replace(/\.[^.]+$/, '.jpg'));
      if (resizedFile) {
        setImageFile(resizedFile);
      } else {
        setError('Failed to process image. Try a different file.');
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setError('Could not read image file. Please try a different file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    };
    img.src = objectUrl;
  };

  const uploadImage = async () => {
    if (!imageFile) return form.image_url || null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', imageFile);
      const res = await fetch('/api/v1/kot/menu-item-image', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-chain-id': scope.chain_id,
          'x-property-id': scope.property_id,
          'x-property-code': selectedProperty?.code || ''
        },
        body: fd
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Image upload failed');
      return data.image_url;
    } catch (err) {
      setError(err.message || 'Image upload failed');
      return form.image_url || null;
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    setImagePreview('');
    setImageFile(null);
    setForm((prev) => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const saveRow = async () => {
    setError('');
    if (!scope.chain_id || !scope.property_id) return setError('Select property first');
    if (!scope.outlet_id) return setError('Select outlet first');
    if (!String(form.name || '').trim()) return setError('Item name is required');
    const basePrice = Number(form.base_price || 0);
    if (Number.isNaN(basePrice) || basePrice < 0) return setError('Base price must be zero or positive');
    const selectedTax = taxRows.find((row) => String(row.id) === String(form.tax_id));

    // Upload image first if a new file is selected
    let imageUrl = form.image_url || null;
    if (imageFile) {
      imageUrl = await uploadImage();
    }

    try {
      const res = await fetch(editingId ? `/api/v1/kot/masters/menu-items/${editingId}` : '/api/v1/kot/masters/menu-items', {
        method: editingId ? 'PUT' : 'POST',
        headers: { ...scopeHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sku: form.sku || null,
          name: form.name,
          base_price: basePrice,
          kot_food_category_id: form.category_id ? Number(form.category_id) : null,
          tax_regime: selectedTax ? String(selectedTax.tax_regime || 'NONE').toUpperCase() : 'NONE',
          tax_id: form.tax_id ? Number(form.tax_id) : null,
          is_veg: form.is_veg === true,
          is_active: form.is_active !== false,
          image_url: imageUrl,
          is_bestseller: form.is_bestseller === true,
          is_spicy: form.is_spicy === true,
          is_jain: form.is_jain === true
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(editingId ? 'Item updated successfully' : 'Item created successfully');
      setEditingId('');
      setForm({ sku: '', name: '', base_price: 0, category_id: '', tax_id: '', is_veg: false, is_active: true, image_url: '', is_bestseller: false, is_spicy: false, is_jain: false });
      setImagePreview('');
      setImageFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const editRow = (row) => {
    setEditingId(row.id);
    setForm({
      sku: row.sku || '',
      name: row.name || '',
      base_price: Number(row.base_price || 0),
      category_id: row.category_id ? String(row.category_id) : '',
      tax_id: row.tax_id ? String(row.tax_id) : '',
      is_veg: row.is_veg === true,
      is_active: row.is_active !== false,
      image_url: row.image_url || '',
      is_bestseller: row.is_bestseller === true,
      is_spicy: row.is_spicy === true,
      is_jain: row.is_jain === true
    });
    setImagePreview(row.image_url || '');
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError('');
    setMessage('');
    scrollToForm();
  };

  const deactivateRow = async (id, name) => {
    if (!window.confirm(`Deactivate item "${name}"?`)) return;
    setError('');
    try {
      const res = await fetch(`/api/v1/kot/masters/menu-items/${id}`, { method: 'DELETE', headers: scopeHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Deactivate failed');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const cancelEdit = () => {
    setEditingId('');
    setForm({ sku: '', name: '', base_price: 0, category_id: '', tax_id: '', is_veg: false, is_active: true, image_url: '', is_bestseller: false, is_spicy: false, is_jain: false });
    setImagePreview('');
    setImageFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const inputStyle = { padding: '0.55rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const thumbStyle = { width: '40px', height: '40px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #dcdde1' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)' }}>KOT Item Master</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <h3>Property & Outlet</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: '0.7rem' }}>
          <input
            value={selectedProperty ? `${selectedProperty.code || ''} ${selectedProperty.name || ''}`.trim() : 'No property selected'}
            readOnly
            style={{ ...inputStyle, background: '#ecf0f1' }}
          />
          <select value={scope.outlet_id} onChange={(e) => setScope((prev) => ({ ...prev, outlet_id: e.target.value }))} style={inputStyle}>
            <option value="">Select Outlet</option>
            {outlets.map((o) => <option key={o.id} value={o.id}>{o.code} - {o.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.7rem', alignItems: 'center' }}>
          <button type="button" onClick={downloadTemplate}
            style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: '1px solid #16a34a', background: '#fff', color: '#166534', fontWeight: 600, cursor: 'pointer', fontSize: '0.85rem' }}>
            ⬇ Download Excel Template
          </button>
          <input ref={importInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleImportFile} style={{ display: 'none' }} />
          <button type="button" onClick={() => { if (!scope.outlet_id) return setError('Select an outlet first'); importInputRef.current?.click(); }} disabled={importing}
            style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, cursor: importing ? 'default' : 'pointer', fontSize: '0.85rem', opacity: importing ? 0.6 : 1 }}>
            {importing ? 'Importing…' : '⬆ Import from Excel'}
          </button>
          <span style={{ fontSize: '0.76rem', color: '#6b7280' }}>Download the template, fill it, then upload. New categories are created automatically.</span>
        </div>
        {importResult && (
          <div style={{ marginTop: '0.7rem', border: '1px solid #dbeafe', background: '#f8fafc', borderRadius: '8px', padding: '0.7rem 0.9rem', fontSize: '0.82rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
              <b style={{ color: '#1e3a5f' }}>Import Summary</b>
              <button type="button" onClick={() => setImportResult(null)} style={{ background: 'none', border: 'none', fontSize: '1.1rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }} aria-label="Dismiss">×</button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1.2rem' }}>
              <span>Rows read: <b>{importResult.total}</b></span>
              <span style={{ color: '#166534' }}>Added: <b>{importResult.created}</b></span>
              <span style={{ color: '#1d4ed8' }}>Updated: <b>{importResult.updated}</b></span>
              <span style={{ color: '#7c3aed' }}>New categories: <b>{importResult.categories_created}</b></span>
              {importResult.tax_unmatched > 0 && <span style={{ color: '#b45309' }}>Tax unmatched: <b>{importResult.tax_unmatched}</b></span>}
              {importResult.errors?.length > 0 && <span style={{ color: '#b91c1c' }}>Skipped: <b>{importResult.errors.length}</b></span>}
            </div>
            {importResult.errors?.length > 0 && (
              <div style={{ marginTop: '0.5rem', maxHeight: '180px', overflowY: 'auto', border: '1px solid #fecaca', borderRadius: '6px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#fef2f2', color: '#991b1b' }}>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Row</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Item</th>
                      <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Reason skipped</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.errors.map((e, i) => (
                      <tr key={i} style={{ borderTop: '1px solid #fee2e2' }}>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{e.row}</td>
                        <td style={{ padding: '0.3rem 0.5rem' }}>{e.name || '—'}</td>
                        <td style={{ padding: '0.3rem 0.5rem', color: '#b91c1c' }}>{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="filter-bar" ref={formRef} style={{ marginBottom: '1rem', ...editHighlightStyle(editHighlight) }}>
        <h3>{editingId ? 'Edit Item' : 'Add Item'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.7rem' }}>
          <input placeholder="SKU (optional)" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} style={inputStyle} />
          <input placeholder="Item Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          <input type="number" min="0" step="0.01" placeholder="Base Price" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} style={inputStyle} />
          <select value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} style={inputStyle}>
            <option value="">No Tax</option>
            {taxRows.map((row) => (
              <option key={row.id} value={row.id}>{row.tax_name} [{String(row.tax_regime || 'NONE').toUpperCase()}] ({Number(row.tax_percent || 0).toFixed(2)}%)</option>
            ))}
          </select>
          <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} style={inputStyle}>
            <option value="">-- No Category --</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>{c.kotfoodcategory}</option>
            ))}
          </select>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_veg} onChange={(e) => setForm({ ...form, is_veg: e.target.checked })} />
            Veg
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_bestseller} onChange={(e) => setForm({ ...form, is_bestseller: e.target.checked })} />
            ⭐ Bestseller
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_spicy} onChange={(e) => setForm({ ...form, is_spicy: e.target.checked })} />
            🌶️ Spicy
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_jain} onChange={(e) => setForm({ ...form, is_jain: e.target.checked })} />
            Jain
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
            Active
          </label>
        </div>

        {/* ── Image Upload Section ── */}
        <div style={{ marginTop: '0.8rem', padding: '0.75rem', border: '1px dashed #b2bec3', borderRadius: '8px', background: '#f8f9fa' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 auto' }}>
              <label style={{ fontWeight: 600, fontSize: '0.85rem', color: '#2d3436', display: 'block', marginBottom: '0.3rem' }}>
                Item Image (optional)
              </label>
              <div style={{ fontSize: '0.72rem', color: '#636e72', marginBottom: '0.4rem', lineHeight: 1.4 }}>
                Square image (1:1 aspect ratio) &bull; Max 2 MB &bull; JPG, PNG, or WebP only &bull; Recommended: 400x400px
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp"
                onChange={(e) => validateAndPreviewImage(e.target.files?.[0])}
                style={{ fontSize: '0.82rem' }}
              />
            </div>
            {/* Preview */}
            {imagePreview && (
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--theme-primary-dark)' }}
                />
                <button
                  type="button"
                  onClick={clearImage}
                  title="Remove image"
                  style={{
                    position: 'absolute', top: '-6px', right: '-6px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    border: 'none', background: '#e74c3c', color: '#fff',
                    cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1
                  }}>
                  X
                </button>
              </div>
            )}
          </div>
        </div>

        <div style={{ marginTop: '0.8rem', display: 'flex', gap: '0.5rem' }}>
          <button onClick={saveRow} disabled={uploading} className="btn-primary" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.7 : 1 }}>
            {uploading ? 'Uploading...' : editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button onClick={cancelEdit} className="btn-muted" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Bulk Update {selectedIds.length > 0 && <span style={{ fontSize: '0.8rem', color: '#2563eb', fontWeight: 600 }}>({selectedIds.length} ticked)</span>}</h3>
          <button type="button" onClick={() => { setBulkOpen((o) => !o); setBulkPreview(null); }}
            style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: '1px solid var(--theme-primary)', background: bulkOpen ? 'var(--theme-primary)' : '#fff', color: bulkOpen ? '#fff' : 'var(--theme-primary)', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
            {bulkOpen ? 'Close' : 'Open Bulk Update'}
          </button>
        </div>
        {bulkOpen && (
          <div style={{ marginTop: '0.8rem', display: 'grid', gap: '0.7rem' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
              <b style={{ fontSize: '0.85rem' }}>Apply to:</b>
              <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}><input type="radio" checked={bulk.scope === 'ALL'} onChange={() => updateBulk({ scope: 'ALL' })} /> All items</label>
              <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}><input type="radio" checked={bulk.scope === 'CATEGORY'} onChange={() => updateBulk({ scope: 'CATEGORY' })} /> Category</label>
              {bulk.scope === 'CATEGORY' && (
                <select value={bulk.category_id} onChange={(e) => updateBulk({ category_id: e.target.value })} style={inputStyle}>
                  <option value="">-- pick category --</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.kotfoodcategory}</option>)}
                </select>
              )}
              <label style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}><input type="radio" checked={bulk.scope === 'ITEMS'} onChange={() => updateBulk({ scope: 'ITEMS' })} /> Ticked items ({selectedIds.length})</label>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.7rem', alignItems: 'center' }}>
              <b style={{ fontSize: '0.85rem' }}>Operation:</b>
              <select value={bulk.operation} onChange={(e) => updateBulk({ operation: e.target.value })} style={inputStyle}>
                <option value="PRICE_CHANGE">Change price</option>
                <option value="SET_TAX">Set tax</option>
                <option value="SET_CATEGORY">Change category</option>
                <option value="SET_ACTIVE">Activate / Deactivate</option>
              </select>
              {bulk.operation === 'PRICE_CHANGE' && (
                <>
                  <select value={bulk.price_mode} onChange={(e) => updateBulk({ price_mode: e.target.value })} style={inputStyle}>
                    <option value="PERCENT">By percent %</option>
                    <option value="AMOUNT">By amount ±</option>
                    <option value="SET">Set to</option>
                  </select>
                  <input type="number" step="0.01" placeholder={bulk.price_mode === 'PERCENT' ? 'e.g. 10 or -5' : 'value'} value={bulk.price_value} onChange={(e) => updateBulk({ price_value: e.target.value })} style={{ ...inputStyle, width: '120px' }} />
                  <select value={bulk.rounding} onChange={(e) => updateBulk({ rounding: e.target.value })} style={inputStyle}>
                    <option value="NONE">No rounding</option>
                    <option value="1">Round to 1</option>
                    <option value="5">Round to 5</option>
                    <option value="10">Round to 10</option>
                  </select>
                </>
              )}
              {bulk.operation === 'SET_TAX' && (
                <select value={bulk.tax_id} onChange={(e) => updateBulk({ tax_id: e.target.value })} style={inputStyle}>
                  <option value="">No Tax</option>
                  {taxRows.map((row) => <option key={row.id} value={row.id}>{row.tax_name} [{String(row.tax_regime || 'NONE').toUpperCase()}] ({Number(row.tax_percent || 0).toFixed(2)}%)</option>)}
                </select>
              )}
              {bulk.operation === 'SET_CATEGORY' && (
                <select value={bulk.target_category_id} onChange={(e) => updateBulk({ target_category_id: e.target.value })} style={inputStyle}>
                  <option value="">-- target category --</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.kotfoodcategory}</option>)}
                </select>
              )}
              {bulk.operation === 'SET_ACTIVE' && (
                <select value={bulk.is_active ? 'Y' : 'N'} onChange={(e) => updateBulk({ is_active: e.target.value === 'Y' })} style={inputStyle}>
                  <option value="Y">Activate</option>
                  <option value="N">Deactivate</option>
                </select>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" onClick={() => runBulk(true)} disabled={bulkBusy}
                style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: '1px solid #64748b', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
                Preview
              </button>
              <button type="button" onClick={() => runBulk(false)} disabled={bulkBusy || !bulkPreview || bulkPreview.applied} title={!bulkPreview ? 'Preview first' : ''}
                style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, cursor: (bulkBusy || !bulkPreview || bulkPreview?.applied) ? 'default' : 'pointer', fontSize: '0.82rem', opacity: (!bulkPreview || bulkPreview?.applied) ? 0.5 : 1 }}>
                {bulkBusy ? 'Working…' : bulkPreview?.applied ? 'Applied ✓' : 'Apply'}
              </button>
            </div>
            {bulkPreview && (
              <div style={{ border: '1px solid #dbeafe', background: '#f8fafc', borderRadius: '8px', padding: '0.6rem 0.8rem' }}>
                <div style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>
                  <b>{bulkPreview.applied ? 'Applied to' : 'Will affect'} {bulkPreview.affected_count} item(s).</b> {bulkPreview.applied ? '' : ' Review the sample, then click Apply.'}
                </div>
                {bulkPreview.sample?.length > 0 && (
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ background: '#eef2ff' }}>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'left' }}>Item</th>
                          <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>Current price</th>
                          {bulkPreview.operation === 'PRICE_CHANGE' && <th style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>New price</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {bulkPreview.sample.map((s) => (
                          <tr key={s.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '0.3rem 0.5rem' }}>{s.name}</td>
                            <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right' }}>{Number(s.base_price).toFixed(2)}</td>
                            {bulkPreview.operation === 'PRICE_CHANGE' && <td style={{ padding: '0.3rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#166534' }}>{s.new_price != null ? Number(s.new_price).toFixed(2) : ''}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bulkPreview.affected_count > bulkPreview.sample.length && (
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.3rem' }}>…and {bulkPreview.affected_count - bulkPreview.sample.length} more.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="filter-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
          <h3 style={{ margin: 0 }}>
            Items
            {rows.length > 0 && (
              <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500, marginLeft: '0.5rem' }}>
                {searchTerm ? `${visibleRows.length} of ${rows.length}` : rows.length}
              </span>
            )}
          </h3>
          <div style={{ position: 'relative', flex: '0 1 300px', minWidth: 200 }}>
            <input
              type="search"
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
              placeholder="Search items by name, SKU or tag"
              aria-label="Search items"
              style={{ width: '100%', boxSizing: 'border-box', padding: '0.45rem 0.7rem 0.45rem 2rem', border: '1px solid #cbd5e0', borderRadius: 6, fontSize: '0.85rem' }}
            />
            <span style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }}>⌕</span>
          </div>
        </div>
        {rows.length === 0 ? (
          <p>No items found</p>
        ) : visibleRows.length === 0 ? (
          <p>No items match "{itemSearch.trim()}".</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={{ padding: '0.6rem', textAlign: 'center' }} title="Select all for Bulk Update">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll} />
                </th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Image</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>SKU</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Base Price</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tax</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Veg</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Tags</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Active</th>
                <th style={{ padding: '0.6rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dcdde1', background: selectedIds.includes(r.id) ? '#eff6ff' : 'transparent' }}>
                  <td style={{ padding: '0.6rem', textAlign: 'center' }}>
                    <input type="checkbox" checked={selectedIds.includes(r.id)} onChange={() => toggleSelect(r.id)} />
                  </td>
                  <td style={{ padding: '0.6rem' }}>
                    {r.image_url ? (
                      <img src={r.image_url} alt={r.name} style={thumbStyle} />
                    ) : (
                      <div style={{ ...thumbStyle, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b2bec3', fontSize: '0.65rem' }}>
                        No img
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '0.6rem' }}>{r.sku || '-'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.name}</td>
                  <td style={{ padding: '0.6rem' }}>{Number(r.base_price || 0).toFixed(2)}</td>
                  <td style={{ padding: '0.6rem' }}>{r.tax_regime && r.tax_regime !== 'NONE' ? `${r.tax_regime} (${r.tax_id || '-'})` : 'NONE'}</td>
                  <td style={{ padding: '0.6rem' }}>{r.is_veg ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem' }}>
                    {r.is_bestseller && <span title="Bestseller">⭐</span>}{' '}
                    {r.is_spicy && <span title="Spicy">🌶️</span>}{' '}
                    {r.is_jain && <span style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>Jain</span>}
                  </td>
                  <td style={{ padding: '0.6rem' }}>{r.is_active ? 'Yes' : 'No'}</td>
                  <td style={{ padding: '0.6rem', display: 'flex', gap: '0.5rem' }}>
                    <button onClick={() => editRow(r)} style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', background: '#3498db', color: '#fff', cursor: 'pointer' }}>Edit</button>
                    <button onClick={() => deactivateRow(r.id, r.name)} className="btn-danger" style={{ padding: '0.55rem 0.9rem', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Deactivate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default KotItemMaster;
